import { BundleSearchResponse, useLazyGetBundleSearchQuery } from '@/api';
import {
  makeSelectBundlePageIdByPageIndex,
  makeSelectBundlePageIndexByPageId,
  resetBundleSearch,
  selectBundlePageIds,
  selectBundleSearchResults,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectViewportPageIndexInStore,
  setBundleSearchFocusedPosition,
  setBundleSearchHighlightPageId,
  setBundleSearchText,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { QueryStatus } from '@reduxjs/toolkit/query';
import {
  ChangeEvent,
  KeyboardEvent as SyntheticKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useViewportPageIndexContext } from './useViewportPageIndexContext';
import { InputSearchHandle } from '@repo/atomic-components';

export const findNearestSearchResultForwardOnly = (
  bundlePageIds: string[],
  searchResultsPagesId: string[],
  currentPageId: string,
): string | undefined => {
  // Current page index in bundlePageIds
  const currentIndex = bundlePageIds.indexOf(currentPageId);

  // If no id in bundlePageIds
  if (currentIndex === -1 || searchResultsPagesId.length === 0) {
    return undefined;
  }

  // Check if current page is in search results
  if (searchResultsPagesId.includes(currentPageId)) {
    return currentPageId;
  }

  let nearestForwardId: string | undefined;
  let minForwardDistance = Infinity;

  // Search forward
  for (const searchId of searchResultsPagesId) {
    const searchIndex = bundlePageIds.indexOf(searchId);
    if (searchIndex > currentIndex) {
      const distance = searchIndex - currentIndex;
      if (distance < minForwardDistance) {
        minForwardDistance = distance;
        nearestForwardId = searchId;
      }
    }
  }

  // If no forward result found and it's the last element, search backward
  if (!nearestForwardId) {
    let nearestBackwardId: string | undefined;
    let minBackwardDistance = Infinity;

    for (const searchId of searchResultsPagesId) {
      const searchIndex = bundlePageIds.indexOf(searchId);
      if (searchIndex < currentIndex) {
        const distance = currentIndex - searchIndex;
        if (distance < minBackwardDistance) {
          minBackwardDistance = distance;
          nearestBackwardId = searchId;
        } else if (distance === minBackwardDistance) {
          // If distance is equal use more close to end in bundlePageIds (larger index)
          if (nearestBackwardId === undefined || searchIndex > bundlePageIds.indexOf(nearestBackwardId)) {
            nearestBackwardId = searchId;
          }
        }
      }
    }
    return nearestBackwardId;
  }

  return nearestForwardId;
};

export const findPositionIndexInDuplicateSubarray = (
  searchResultList: string[],
  searchResultIndex: number,
): number | null => {
  if (searchResultIndex < 0 || searchResultIndex >= searchResultList.length) {
    return null; // Index out of range
  }

  const targetElement = searchResultList[searchResultIndex];
  let duplicateCount = 0;

  for (let i = 0; i < searchResultList.length; i++) {
    if (searchResultList[i] === targetElement) {
      if (i === searchResultIndex) {
        return duplicateCount + 1; // Return the item's order number in the duplicate list
      }
      duplicateCount++;
    }
  }

  // Never happen
  return null;
};

export const usePdfPageSearch = () => {
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);

  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);

  const documentReviewPageContext = useViewportPageIndexContext();
  const searchInputRef = useRef<InputSearchHandle>(null);
  const [searchResultIndex, setSearchResultIndex] = useState<number>(0);

  const dispatch = useAppDispatch();
  const [trigger, { isLoading, requestId, status, reset, isSuccess }] = useLazyGetBundleSearchQuery();
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const bundleSearchResults = useAppSelector(selectBundleSearchResults);

  const handleSearchInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isSuccess) {
      handleResetClick();
    }

    const newInputValue = event.target.value;
    if (activeBundleType) {
      dispatch(
        setBundleSearchText({
          key: activeBundleType,
          data: newInputValue,
        }),
      );
    }
  };

  const handleSearchInputKeyDown = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (!bundleId) {
      return;
    }

    if (event.key === 'Enter' && searchInputRef.current) {
      searchInputRef.current.disabled = true;
      trigger({ bundleId: bundleId, searchTerm: searchInputRef.current.value })
        .catch((error) => {
          console.error('Error during search:', error);
        })
        .finally(() => {
          if (searchInputRef.current) {
            searchInputRef.current.disabled = false;
          }
        });
    }
  };

  const filteredBundleSearchResults = useMemo((): BundleSearchResponse => {
    // Filter pageIds: only those present in bundlePageIds
    const filteredPageIds = bundleSearchResults.pageIds.filter((id) => bundlePageIds.includes(id));

    // Filter resultsPerPage: only keys present in bundlePageIds
    const filteredResultsPerPage: Record<string, number> = {};
    for (const key of Object.keys(bundleSearchResults.resultsPerPage)) {
      if (bundlePageIds.includes(key)) {
        filteredResultsPerPage[key] = bundleSearchResults.resultsPerPage[key];
      }
    }

    return {
      pageIds: filteredPageIds,
      resultsPerPage: filteredResultsPerPage,
    };
  }, [bundlePageIds, bundleSearchResults]);

  const searchResultList = useMemo(() => {
    const repeatedIds: string[] = [];

    for (const pageId of filteredBundleSearchResults.pageIds) {
      const count = filteredBundleSearchResults.resultsPerPage[pageId] || 0;
      for (let i = 0; i < count; i++) {
        repeatedIds.push(pageId);
      }
    }

    return repeatedIds;
  }, [filteredBundleSearchResults]);

  const totalSearchResults = useMemo(() => {
    return Object.values(filteredBundleSearchResults.resultsPerPage).reduce((sum, count) => sum + count, 0);
  }, [filteredBundleSearchResults]);

  const handleChangeViewportPageIndex = (newViewportPageIndex: number) => {
    if (newViewportPageIndex >= 0 && activeBundleType) {
      dispatch(
        setBundleSearchHighlightPageId({
          key: activeBundleType,
          data: searchResultList[newViewportPageIndex],
        }),
      );

      const positionIndexInDuplicateSubarray = findPositionIndexInDuplicateSubarray(
        searchResultList,
        newViewportPageIndex,
      );
      dispatch(
        setBundleSearchFocusedPosition({
          key: activeBundleType,
          data: positionIndexInDuplicateSubarray,
        }),
      );

      const newPageIndex = getBundlePageIndexByPageId(searchResultList[newViewportPageIndex]);

      if (newPageIndex !== viewportPageIndexInStore) {
        documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(newPageIndex);
      }
    }
  };

  const handlePreviousClick = () => {
    setSearchResultIndex((prev) => {
      const newIndex = prev - 1 >= 0 ? prev - 1 : searchResultList.length - 1;
      handleChangeViewportPageIndex(newIndex);
      return newIndex;
    });
  };

  const handleNextClick = () => {
    setSearchResultIndex((prev) => {
      const newIndex = prev + 1 <= searchResultList.length - 1 ? prev + 1 : 0;
      handleChangeViewportPageIndex(newIndex);
      return newIndex;
    });
  };

  const handleResetClick = useCallback(() => {
    /**
     * If the CSS Custom Highlight API is not supported, display a message and bail-out.
     */
    if (!CSS.highlights) {
      console.error('CSS Custom Highlight API not supported.');
      return;
    }
    /**
     * Clear the HighlightRegistry to remove the previous search results. CSS.highlights.clear();
     */
    CSS.highlights.delete('search-results');
    CSS.highlights.delete('focused-search-result');

    dispatch(resetBundleSearch());

    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  useEffect(() => {
    if (status === QueryStatus.fulfilled && searchResultList.length) {
      const pageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);
      const closestPageId = findNearestSearchResultForwardOnly(
        bundlePageIds,
        filteredBundleSearchResults.pageIds,
        pageId,
      );

      if (closestPageId && activeBundleType) {
        dispatch(
          setBundleSearchHighlightPageId({
            key: activeBundleType,
            data: closestPageId,
          }),
        );

        const closestViewportPageIndex = getBundlePageIndexByPageId(closestPageId);

        const positionIndexInDuplicateSubarray = findPositionIndexInDuplicateSubarray(
          searchResultList,
          searchResultIndex,
        );

        dispatch(
          setBundleSearchFocusedPosition({
            key: activeBundleType,
            data: positionIndexInDuplicateSubarray,
          }),
        );

        const indexInSearchResult = searchResultList.indexOf(closestPageId);
        setSearchResultIndex(indexInSearchResult);

        if (closestViewportPageIndex !== viewportPageIndexInStore) {
          documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(closestViewportPageIndex);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, status, searchResultList]);

  return {
    searchInputRef,
    isLoading,
    isSuccess,
    totalSearchResults,
    searchResultPageIds: filteredBundleSearchResults.pageIds,
    searchResultIndex,
    handlePreviousClick,
    handleNextClick,
    handleResetClick,
    handleSearchInputValueChange,
    handleSearchInputKeyDown,
  };
};
